import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import CheckboxGroup from "../../rhf-inputs/CheckboxGroup";
import TextInput from "../../rhf-inputs/TextInput";
import SelectInput from "../../rhf-inputs/SelectInput";
import PhoneInput from "../../rhf-inputs/PhoneInput";
import countriesData from "../../countries.json";
import { apiRequest } from "../../../utils";
import { KybDescription } from "../KybDescription";
import progressIcon from "../../../assets/icons/progress.svg?raw&inline";

const FUENTE_INGRESOS = [
  "ahorros",
  "comercializacion de bienes y servicios",
  "dividentos",
  "aporte de accionistas",
  "otros",
] as const;
type FuenteIngresosOption = (typeof FUENTE_INGRESOS)[number];
const FUENTE_INGRESOS_OTROS: FuenteIngresosOption = "otros";

// Zod schema for company info and financial profile validation
const companyFinancialSchema = z
  .object({
    company_address: z
      .string()
      .min(1, "kyb_flow.company_financial_info.address_required"),
    company_phone: z
      .string()
      .min(1, "kyb_flow.company_financial_info.phone_required"),
    income_sources: z
      .array(z.enum(FUENTE_INGRESOS))
      .min(1, "kyb_flow.company_financial_info.income_sources.required"),
    income_sources_other: z.string().optional(),
    income_countries: z
      .array(z.string())
      .min(1, "kyb_flow.company_financial_info.income_countries.required"),
    economic_activity_countries: z
      .array(z.string())
      .min(
        1,
        "kyb_flow.company_financial_info.economic_activity_countries.required"
      ),
    annual_income_range: z
      .string()
      .min(1, "kyb_flow.company_financial_info.annual_income.required"),
  })
  .refine(
    (data) => {
      // If "otros" is selected, income_sources_other must be provided
      if (data.income_sources.includes(FUENTE_INGRESOS_OTROS)) {
        return (
          data.income_sources_other !== undefined &&
          data.income_sources_other.trim().length > 0
        );
      }
      return true;
    },
    {
      message: "kyb_flow.company_financial_info.income_sources.other_required",
      path: ["income_sources_other"],
    }
  );

type CompanyFinancialFormData = z.infer<typeof companyFinancialSchema>;

// Income range options
const incomeRangeOptions = [
  { value: "0-50000", label: "$0 - $50,000" },
  { value: "50000-100000", label: "$50,000 - $100,000" },
  { value: "100000-250000", label: "$100,000 - $250,000" },
  { value: "250000-500000", label: "$250,000 - $500,000" },
  { value: "500000-1000000", label: "$500,000 - $1,000,000" },
  { value: "1000000+", label: "$1,000,000+" },
];

interface FinancialProfileStepProps {
  data?: Partial<CompanyFinancialFormData>;
  claimRequestId?: number;
  organization?: { nombre: string; [key: string]: unknown };
  userId?: number;
  privacyPolicyAccepted: boolean;
  onStepCompleted: () => void;
}

const FinancialProfileStep = ({
  data,
  claimRequestId,
  organization,
  userId,
  privacyPolicyAccepted,
  onStepCompleted,
}: FinancialProfileStepProps) => {
  const { t, i18n } = useTranslation();
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<CompanyFinancialFormData>({
    resolver: zodResolver(companyFinancialSchema),
    defaultValues: {
      company_address: data?.company_address || "",
      company_phone: data?.company_phone || "",
      income_sources: data?.income_sources || [],
      income_sources_other: data?.income_sources_other || "",
      income_countries: data?.income_countries || [],
      economic_activity_countries: data?.economic_activity_countries || [],
      annual_income_range: data?.annual_income_range || "",
    },
  });

  const selectedIncomeSources = watch("income_sources");
  const hasOtrosSelected =
    selectedIncomeSources?.includes(FUENTE_INGRESOS_OTROS) || false;

  // Income source options for CheckboxGroup
  const fuenteIngresosOptions = FUENTE_INGRESOS.map((value) => ({
    value,
    label: t(`kyb_flow.company_financial_info.${value}`),
  }));

  const handleEntityName = () => {
    if (organization) {
      return organization.nombre.length > 30
        ? `${organization.nombre.slice(0, 30)}...`
        : organization.nombre;
    }
    return "";
  };

  const onSubmit = async (formData: CompanyFinancialFormData) => {
    setSaveError(null);
    try {
      if (!claimRequestId ) {
        throw new Error("Claim request ID is required");
      }

      const payload = {
        claim_request: {
          user_id: userId,
          organization_id: organization?.id,
          data: {
            company_address: formData.company_address,
            company_phone: formData.company_phone,
            income_sources: formData.income_sources,
            income_sources_other: formData.income_sources_other || null,
            income_countries: formData.income_countries,
            economic_activity_countries: formData.economic_activity_countries,
            annual_income_range: formData.annual_income_range,
          }
        }
      };

      const response = await apiRequest(
        `/business_claim/claim_request`,
        "POST",
        payload
      );

      if (response.success) {
        onStepCompleted();
      } else {
        setSaveError(response.error || "Error al guardar los datos");
      }
    } catch (error) {
      console.error("Error saving financial profile:", error);
      setSaveError(
        "Error al guardar los datos. Por favor, intente nuevamente."
      );
    }
  };

  // Country options for SelectInput (use Spanish or English based on current language)
  const countryOptions = countriesData.map((country) => ({
    value: country.name_es,
    label: i18n.language === "es" ? country.name_es : country.name_en,
  }));

  // Determine button text based on privacyPolicyAccepted
  const buttonText = privacyPolicyAccepted
    ? t("kyb_flow.steps.step_3_button_beneficiary")
    : t("kyb_flow.steps.step_3_button");

  return (
    <div
      className="row d-flex mt-5 justify-content-center"
      style={{ maxWidth: 1000 }}
    >
      <div className="w-100 text-center mb-5 position-relative">
        <div className="badge rounded-pill text-uppercase size-12 font-weight-600 w-auto bg-kyb-pill text-dark">
          {handleEntityName()}
        </div>
      </div>

      <span className="size-10 font-weight-600 hyperlink">
        {t("kyb_flow.steps_count", {
          step: 3,
          total: privacyPolicyAccepted ? 4 : 5,
        })}
      </span>

      <KybDescription
        title={t("kyb_flow.company_financial_info.title")}
        description=""
      />

      <form onSubmit={handleSubmit(onSubmit)} className="col-10">
        <div className="mt-5 d-flex flex-column gap-4 text-start">
          {/* Company Information Section */}

          <div
            className="p-4"
            style={{ backgroundColor: "#F8F9FA", borderRadius: "13px" }}
          >
            <h5 className="size-16 font-weight-600">
              {t("kyb_flow.company_financial_info.address_phone")}
            </h5>
            <p className="size-13 text-muted">
              {t("kyb_flow.company_financial_info.address_description")}
            </p>
            <div className="d-flex flex-column gap-3">
              <TextInput
                name="company_address"
                control={control}
                label={t("kyb_flow.company_financial_info.address_label")}
                placeholder={t(
                  "kyb_flow.company_financial_info.address_placeholder"
                )}
              />
              <PhoneInput
                name="company_phone"
                control={control}
                label={t("kyb_flow.company_financial_info.phone_placeholder")}
                placeholder={t(
                  "kyb_flow.company_financial_info.phone_placeholder"
                )}
              />
            </div>
          </div>

          {/* Financial Profile Section */}
          <div className="mt-4">
            <h5 className="size-16 font-weight-600 mb-2">
              {t("kyb_flow.company_financial_info.financial_profile_title")}
            </h5>
            <p className="size-14 text-muted mb-4">
              {t(
                "kyb_flow.company_financial_info.financial_profile_description"
              )}
            </p>

            {/* Income Sources Section */}
            <div className="mb-4">
              <CheckboxGroup
                name="income_sources"
                control={control}
                options={fuenteIngresosOptions}
              />

              {/* Conditional "Otros" Input */}
              {hasOtrosSelected && (
                <div className="mt-3">
                  <TextInput
                    name="income_sources_other"
                    control={control}
                    placeholder={t(
                      "kyb_flow.company_financial_info.income_sources.other_placeholder"
                    )}
                  />
                </div>
              )}
            </div>

            <p className="size-16 font-weight-600 mb-3 text-black">
              {t("kyb_flow.company_financial_info.income_sources_title")}
            </p>

            {/* Income Countries Multi-Select */}
            <div className="mb-3">
              <SelectInput
                name="income_countries"
                control={control}
                label={t(
                  "kyb_flow.company_financial_info.income_countries.label"
                )}
                placeholder={t(
                  "kyb_flow.company_financial_info.income_countries.placeholder"
                )}
                options={countryOptions}
                isMulti={true}
              />
            </div>

            {/* Economic Activity Countries Multi-Select */}
            <div className="mb-3">
              <SelectInput
                name="economic_activity_countries"
                control={control}
                label={t(
                  "kyb_flow.company_financial_info.economic_activity_countries.label"
                )}
                placeholder={t(
                  "kyb_flow.company_financial_info.economic_activity_countries.placeholder"
                )}
                options={countryOptions}
                isMulti={true}
              />
            </div>

            {/* Annual Income Amount Single Select */}
            <div className="mb-4">
              <SelectInput
                name="annual_income_range"
                control={control}
                label={t("kyb_flow.company_financial_info.annual_income.label")}
                placeholder={t(
                  "kyb_flow.company_financial_info.annual_income.placeholder"
                )}
                options={incomeRangeOptions}
                isMulti={false}
              />
            </div>
          </div>

          {/* Error Message */}
          {saveError && (
            <div className="alert alert-danger" role="alert">
              {saveError}
            </div>
          )}

          {/* Submit Button */}
          <div className="d-flex flex-column align-items-center mt-4">
            <button
              type="submit"
              className="btn btn-black w-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span
                  className="spin"
                  dangerouslySetInnerHTML={{ __html: progressIcon }}
                />
              ) : (
                buttonText
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default FinancialProfileStep;
